import { useContext } from 'react';

import { GlobalPropsContext, useAccountFeatureFlag } from '@/components/page/GlobalPropsContext';
import { useOwnedAppsList } from '@/hooks/api/useOwnedAppsList';
import useAccounts from '@/hooks/useAccounts';

import OnboardingChecklist from './OnboardingChecklist';
import { useIsOnboardingChecklistHeld } from './onboardingChecklistGate';
import useFirstBuildOnboardingChecklist from './useFirstBuildOnboardingChecklist';

// The WFE is a full page load under /app/:slug/workflow_editor; there's no client-side router, so
// "render collapsed on the Workflow Editor" is decided from the pathname at mount.
const isWorkflowEditorPage = () => window.location.pathname.includes('/workflow_editor');

/**
 * Renders the first-build onboarding checklist on every CI page for eligible new users. Mounted once
 * from ProtectedPage (gated by isCiProductArea), so it's page-agnostic: the app slug comes from the
 * user's single owned project — the new-user gate guarantees exactly one — which means it also works
 * on workspace-level CI pages (dashboard, projects list) that carry no app slug in the URL.
 */
const OnboardingChecklistMount = () => {
  const isEnabled = useAccountFeatureFlag('firstBuildOnboardingChecklist');
  const { account } = useContext(GlobalPropsContext);
  const eligible = isEnabled && Boolean(account);

  // "New user" (CI-6313, first-experiment definition): exactly one workspace and one owned project.
  const accountsQuery = useAccounts({ enabled: eligible });
  const hasSingleAccount = accountsQuery.data?.length === 1;
  // Chained behind the workspace count, not run alongside it: everyone who already fails that half
  // of the gate then issues no project query at all.
  // limit 2 is all it takes to tell "exactly one project" from "more than one".
  const appsQuery = useOwnedAppsList(
    { accountType: account?.type === 'user' ? 'User' : 'Organization', slug: account?.slug ?? '' },
    { limit: 2, enabled: eligible && hasSingleAccount && Boolean(account?.slug) },
  );
  const apps = appsQuery.data?.apps;
  const appSlug = apps?.[0]?.slug ?? '';
  const canShow = eligible && hasSingleAccount && apps?.length === 1 && Boolean(appSlug);

  const { steps, isHidden, isDismissalLoading, finish, dismiss } = useFirstBuildOnboardingChecklist({
    appSlug,
    enabled: canShow,
  });

  const isHeldBack = useIsOnboardingChecklistHeld();

  if (!canShow || isDismissalLoading || isHidden || isHeldBack) {
    return null;
  }

  return (
    <OnboardingChecklist
      steps={steps}
      appSlug={appSlug}
      onFinish={finish}
      onDismiss={dismiss}
      defaultExpanded={!isWorkflowEditorPage()}
      position="fixed"
      insetBlockEnd="16"
      insetInlineEnd="16"
      zIndex="popover"
    />
  );
};

export default OnboardingChecklistMount;
